import { ReactElement, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Stack } from '@mui/material'
import { useIsMutating } from '@tanstack/react-query'
import { Form2 } from '@ui/forms'
import CheckSettingsDirtyBlocker from '../MultiEditCheckSettings/components/CheckSettingsDirtyBlocker'
import { checkSettingsRecomputeKey } from '../MultiEditCheckSettings/hooks/useLiveCheckRecompute'
import useSaveSharedCheckSettings from '../MultiEditCheckSettings/hooks/useSaveSharedCheckSettings'
import {
  CheckSettingsFormValues,
  MultiEditCheckSettingsElement,
} from '../MultiEditCheckSettings/types'
import SharedSupportAreaField from './components/SharedSupportAreaField'
import SharedSupportFC90KField from './components/SharedSupportFC90KField'
import SharedSupportKC90Field from './components/SharedSupportKC90Field'
import SharedSupportOverhangField from './components/SharedSupportOverhangField'
import SupportCompressionActions from './components/SupportCompressionActions'
import SupportCompressionFields from './components/SupportCompressionFields'
import SupportCompressionMatrix from './components/SupportCompressionMatrix'
import SupportSceneCheckboxesEffect from './components/SupportSceneCheckboxesEffect'
import {
  SharedSupportCompressionSettings,
  buildIrrelevantSupports,
  buildSharedSupportCompressionDefaults,
  mergeSupportCompressionSettings,
} from './utils'

interface Props {
  elements: MultiEditCheckSettingsElement[]
}

/**
 * Auflagerpressung multi-edit: shows all support fields side by side and
 * stages them for every support of every selected member, previews the
 * resulting support compression utilization per support in a member × support
 * matrix and saves the staged fields member by member.
 */
const MultiEditSupportCompression = ({ elements }: Props): ReactElement => {
  const { projectId } = useParams()

  const { save, isLoading } = useSaveSharedCheckSettings<SharedSupportCompressionSettings>({
    settingsLabel: 'Auflagerpressungs-Einstellungen',
    mergeSettings: mergeSupportCompressionSettings,
  })

  const defaultValues = useMemo<CheckSettingsFormValues<SharedSupportCompressionSettings>>(() => {
    // Resolved once from the saved checks so the live preview cannot shift which
    // supports the edit covers while the user is working.
    const irrelevantSupports = buildIrrelevantSupports(elements)

    return {
      sharedSettings: {
        ...buildSharedSupportCompressionDefaults(elements, irrelevantSupports),
        irrelevantSupports,
      },
      elements,
    }
  }, [elements])

  // The matrix recomputes the checks live while the staged field changes; saving
  // mid-recompute would persist the stale preview.
  const isUpdatingChecks =
    useIsMutating(checkSettingsRecomputeKey(projectId, 'supportCompression')) > 0

  // Memoized so it's only recomputed when `defaultValues` itself changes,
  // rather than re-stringifying the whole elements/checks payload on every
  // render (e.g. while the live recompute is updating isUpdatingChecks).
  const formKey = useMemo(() => JSON.stringify(defaultValues), [defaultValues])

  return (
    <Stack direction="column" spacing={2} width="100%" pt={1}>
      <Form2
        key={formKey}
        defaultValues={defaultValues}
        mode="onChange"
        onSubmit={data => save(data as CheckSettingsFormValues<SharedSupportCompressionSettings>)}
        data-cy="support-compression-form"
      >
        <Stack direction="column" spacing={1}>
          <CheckSettingsDirtyBlocker<SharedSupportCompressionSettings>
            sectionKey="supportCompression"
            ignoreDirtyFields={['excludedSupports', 'irrelevantSupports']}
          />

          <SupportCompressionFields
            fieldEditors={{
              area: <SharedSupportAreaField />,
              overhang: <SharedSupportOverhangField />,
              k_c_90: <SharedSupportKC90Field />,
              f_c_90_k: <SharedSupportFC90KField />,
            }}
          />

          <SupportCompressionMatrix />

          <SupportSceneCheckboxesEffect />

          <SupportCompressionActions isLoading={isLoading} isUpdatingChecks={isUpdatingChecks} />
        </Stack>
      </Form2>
    </Stack>
  )
}

export default MultiEditSupportCompression
